import { identity } from 'ramda'
import { createSelector } from 'reselect'
import {
  mapResponseToResource,
  getFilteredPreProdVehicleList,
} from 'utils/deprecated/search'
import {
  getCanEditViewChecPlusVehicle,
  getCanEditViewM2Vehicle,
  getIsBuilderRole,
} from 'selectors/user'
import { createToJsSelector } from '../utils'

const getSearch = (state, resource) =>
  state.get('searchDeprecated').get(resource)

const getSelectedRecordData = (state, resource) => {
  const search = getSearch(state, resource)
  if (search === undefined) {
    return false
  }
  return {
    selectedRecord: search.get('selectedRecord'),
  }
}
export const getSelectedRecord = createSelector(
  [getSelectedRecordData],
  (search) => ({
    selectedRecord: search.selectedRecord ? search.selectedRecord.toJS() : null,
  })
)

const getSearchResourceData = (state, resource) => {
  const search = getSearch(state, resource)
  const results = search.get('results')

  return {
    searchString: search.get('searchString'),
    submittedSearchString: search.get('submittedSearchString'),
    submittedResourceType: search.get('submittedResourceType'),
    isSearching: search.get('loadingInitialResults'),
    isSearchingAdditional: search.get('loadingAdditionalResults'),
    results: mapResponseToResource(results, resource),
    message: search.get('message'),
    searchResultsPopulated: search.get('searchResultsPopulated'),
    buttonMessage: search.get('buttonMessage'),
    buttonState: search.get('buttonState'),
    searchPerformed: search.get('searchPerformed'),
    error: search.get('error'),
    resultsRemaining: search.get('resultsRemaining'),
    resourceType: search.get('resourceType'),
    selectedRecord: search.get('selectedRecord'),
    filters: search.get('filters'),
    totalResults: search.get('totalResults'),
    selectionKeys: search.get('selectionKeys'),
    startIndex: search.get('startIndex'),
    properties: search.get('properties'),
    unsupportedVehicles: search.get('unsupportedVehicles'),
    notFoundVehicles: search.get('notFoundVehicles'),
    m2Vehicles: search.get('m2Vehicles'),
    checPlusVehicles: search.get('checPlusVehicles'),
  }
}

const searchResultsSelector = (state, { resource }) =>
  state.getIn(['searchDeprecated', resource, 'results'])

export const getSearchResults = createToJsSelector(
  createSelector(searchResultsSelector, identity)
)

export const getSearchResource = createSelector(
  [getSearchResourceData],
  (search) => ({
    searchString: search.searchString,
    submittedSearchString: search.submittedSearchString,
    submittedResourceType: search.submittedResourceType || undefined,
    isSearching: search.isSearching,
    isSearchingAdditional: search.isSearchingAdditional,
    resultsRemaining: search.resultsRemaining,
    results: search.results ? search.results.toJS() : null,
    searchResults: search.results ? search.results.toJS() : null,
    message: search.message,
    searchResultsPopulated:
      search.searchResultsPopulated !== undefined
        ? search.searchResultsPopulated
        : false,
    button: {
      message: search.buttonMessage,
      state: search.buttonState,
    },
    searchPerformed: search.searchPerformed,
    error: search.error,
    resourceType: search.resourceType,
    selectedRecord: search.selectedRecord,
    filters: search.filters ? search.filters.toJS() : null,
    totalResults: search.totalResults,
    invalid: search.filters ? search.filters.toJS().invalid : false,
    selectionKeys: search.selectionKeys ? search.selectionKeys.toJS() : null,
    startIndex: search.startIndex,
    properties: search.properties ? search.properties.toJS() : null,
    unsupportedVehicles: search.unsupportedVehicles
      ? search.unsupportedVehicles.toJS()
      : [],
    notFoundVehicles: search.notFoundVehicles
      ? search.notFoundVehicles.toJS()
      : [],
    m2Vehicles: search.m2Vehicles ? search.m2Vehicles.toJS() : [],
    checPlusVehicles: search.checPlusVehicles
      ? search.checPlusVehicles.toJS()
      : [],
  })
)

const getSearchVehiclesSelector = (state, searchResults) => {
  const search = getSearchResource(state, 'vehicles')
  const canEditM2Vehicle = getCanEditViewM2Vehicle(state)
  const canEditChecPlus = getCanEditViewChecPlusVehicle(state)
  const isBuilder = getIsBuilderRole(state)
  let vehicleList = []
  let unauthorizedM2Vehicles = []
  let unauthorizedChecPlusVehicles = []
  let results
  if (search.m2Vehicles.length === 0 && search.checPlusVehicles.length === 0) {
    results = searchResults || search.results
  } else {
    if (search.m2Vehicles && search.m2Vehicles.length > 0) {
      if (canEditM2Vehicle) {
        vehicleList = [...vehicleList, ...search.m2Vehicles]
      } else if (isBuilder) {
        const { preProd, nonPreProd } = getFilteredPreProdVehicleList(
          search.m2Vehicles
        )

        vehicleList = [...vehicleList, ...preProd]
        unauthorizedM2Vehicles = [...unauthorizedM2Vehicles, ...nonPreProd]
      } else {
        unauthorizedM2Vehicles = [
          ...unauthorizedM2Vehicles,
          ...search.m2Vehicles,
        ]
      }
    }

    if (search.checPlusVehicles && search.checPlusVehicles.length > 0) {
      if (canEditChecPlus) {
        vehicleList = [...vehicleList, ...search.checPlusVehicles]
      } else if (isBuilder) {
        const { preProd, nonPreProd } = getFilteredPreProdVehicleList(
          search.checPlusVehicles
        )

        vehicleList = [...vehicleList, ...preProd]
        unauthorizedChecPlusVehicles = [
          ...unauthorizedChecPlusVehicles,
          ...nonPreProd,
        ]
      } else {
        unauthorizedChecPlusVehicles = [
          ...unauthorizedChecPlusVehicles,
          ...search.checPlusVehicles,
        ]
      }
    }

    results = [...vehicleList]
  }

  return {
    data: results,
    unauthorizedM2Vehicles,
    unauthorizedChecPlusVehicles,
  }
}

export const getVehicleSearchResult = createSelector(
  [getSearchVehiclesSelector],
  identity
)

const getSearchedVehiclesForCompareSelector = (state, searchResults) => {
  const search = getSearchResource(state, 'vehicles')
  const canEditM2Vehicle = getCanEditViewM2Vehicle(state)
  const canEditChecPlus = getCanEditViewChecPlusVehicle(state)
  const isBuilder = getIsBuilderRole(state)
  let vehicleList = []
  let results
  if (search.m2Vehicles.length === 0 && search.checPlusVehicles.length === 0) {
    results = searchResults || search.results
  } else {
    if (search.m2Vehicles && search.m2Vehicles.length > 0) {
      if (canEditM2Vehicle) {
        vehicleList = [...vehicleList, ...search.m2Vehicles]
      } else if (isBuilder) {
        const { preProd } = getFilteredPreProdVehicleList(search.m2Vehicles)

        vehicleList = [...vehicleList, ...preProd]
      }
    }

    if (search.checPlusVehicles && search.checPlusVehicles.length > 0) {
      if (canEditChecPlus) {
        vehicleList = [...vehicleList, ...search.checPlusVehicles]
      } else if (isBuilder) {
        const { preProd } = getFilteredPreProdVehicleList(
          search.checPlusVehicles
        )

        vehicleList = [...vehicleList, ...preProd]
      }
    }

    results = [...vehicleList]
  }

  return {
    data: results,
  }
}

export const getSearchedVehiclesForCompare = createSelector(
  [getSearchedVehiclesForCompareSelector],
  identity
)
